import {
  CollapseSection,
  PopoverOpenButton,
  PopoverOpenButtonRef,
} from "@msys/ui";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField as MuiTextField,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { useRef } from "react";
import { RestrictedByProjectPermissionWithDebug } from "../../../auth/RestrictedByProjectPermission.js";
import { AutocompletePopperSlot } from "../../../commons/AutocompletePopperSlot.js";
import { useOrganisationTeams_TeamsQuery } from "../../../main-routes/my-organisation/OrganisationTeams.generated.js";
import {
  useAddProjectTeamMutation,
  useRemoveProjectTeamMutation,
} from "../Projects.generated.js";
import { ProjectOverviewParticipantsBox_ProjectFragment } from "./ProjectOverviewParticipantsBox.generated.js";

interface Props {
  project: ProjectOverviewParticipantsBox_ProjectFragment;
}

export const ProjectTeamsSection = ({ project }: Props) => {
  const { t } = useTranslate("ProjectOverview");
  const popoverRef = useRef<PopoverOpenButtonRef>();

  return (
    <CollapseSection
      title={t("Teams")}
      itemCount={project.teams.length}
      ActionButtons={[
        <RestrictedByProjectPermissionWithDebug
          key="add-team"
          permission="MANAGE_PROJECT"
          project={project}
        >
          <PopoverOpenButton
            popoverRef={popoverRef}
            content={
              <AddTeamPopoverContent
                project={project}
                onClose={() => popoverRef.current?.handleClose()}
              />
            }
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <IconButton size="small">
              <AddIcon color="primary" />
            </IconButton>
          </PopoverOpenButton>
        </RestrictedByProjectPermissionWithDebug>,
      ]}
    >
      <List disablePadding sx={{ marginX: -1 }}>
        {project.teams.map(team => (
          <ProjectTeamRow key={team.id} project={project} team={team} />
        ))}
        {project.teams.length === 0 && (
          <ListItem>
            <ListItemText
              primary={
                <Typography variant="body2" color="text.secondary">
                  {t("No teams linked")}
                </Typography>
              }
            />
          </ListItem>
        )}
      </List>
    </CollapseSection>
  );
};

interface TeamRowProps {
  project: ProjectOverviewParticipantsBox_ProjectFragment;
  team: ProjectOverviewParticipantsBox_ProjectFragment["teams"][number];
}

const ProjectTeamRow = ({ project, team }: TeamRowProps) => {
  const { t } = useTranslate("ProjectOverview");
  const { enqueueSnackbar } = useSnackbar();
  const [removeProjectTeam, { loading }] = useRemoveProjectTeamMutation();

  return (
    <ListItem
      secondaryAction={
        <RestrictedByProjectPermissionWithDebug
          permission="MANAGE_PROJECT"
          project={project}
        >
          <IconButton
            size="small"
            edge="end"
            disabled={loading}
            onClick={async () => {
              try {
                await removeProjectTeam({
                  variables: {
                    input: { projectId: project.id, teamId: team.id },
                  },
                });
                enqueueSnackbar(t("Team removed from project"), {
                  variant: "success",
                });
              } catch (e) {
                enqueueSnackbar((e as Error).message, { variant: "error" });
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </RestrictedByProjectPermissionWithDebug>
      }
    >
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">{team.name}</Typography>
            {!team.isActive && (
              <Chip
                size="small"
                label={t("Deactivated")}
                color="default"
                variant="outlined"
              />
            )}
          </Box>
        }
      />
    </ListItem>
  );
};

function AddTeamPopoverContent({
  project,
  onClose,
}: Props & { onClose: () => void }) {
  const { t } = useTranslate("ProjectOverview");
  const { enqueueSnackbar } = useSnackbar();
  const [addProjectTeam, { loading }] = useAddProjectTeamMutation();

  const { data } = useOrganisationTeams_TeamsQuery({
    variables: { limit: 1000, filters: { includeInactive: false } },
  });

  const teamsList =
    data?.teams?.__typename === "TeamConnection"
      ? data.teams.edges.map(e => e.node)
      : [];

  const linkedTeamIds = new Set(project.teams.map(t => t.id));
  const pickable = teamsList.filter(t => !linkedTeamIds.has(t.id));

  return (
    <Box sx={{ width: 320 }}>
      <Autocomplete
        open
        size="small"
        options={pickable}
        getOptionLabel={option => option.name}
        value={null}
        disabled={loading}
        onChange={async (_e, team) => {
          if (!team) return;
          try {
            await addProjectTeam({
              variables: {
                input: { projectId: project.id, teamId: team.id },
              },
            });
            enqueueSnackbar(t("Team added to project"), {
              variant: "success",
            });
            onClose();
          } catch (e) {
            enqueueSnackbar((e as Error).message, { variant: "error" });
          }
        }}
        renderInput={params => (
          <MuiTextField {...params} placeholder={t("Add team")} />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ py: 0.5 }}
            >
              <ListItemText
                primary={option.name}
                slotProps={{ primary: { variant: "body2" } }}
              />
            </Stack>
          </li>
        )}
        slots={{ popper: AutocompletePopperSlot }}
        disablePortal
        blurOnSelect
        clearOnBlur
      />
    </Box>
  );
}
